import { t } from "i18next";

const ORGANIZATION_PREFERENCES = Object.freeze({
    Lot: {
        label: "Lot",
        value: "Lot",
    },
});

const PREFERENCE_EMPTY_STATE = Object.freeze({
    entity: null,
    localizations: { en: "" },
});

const ROLES = Object.freeze({
    admin: "Admin",
    partnerAdmin: "PartnerAdmin",
    superAdmin: "SuperAdmin",
    user: "User",
    partnerUser: "PartnerUser",
    support: "Support",
});

const ROLES_VERSE = Object.freeze({
    Admin: "User",
    PartnerAdmin: "PartnerUser",
    User: "Admin",
    PartnerUser: "PartnerAdmin",
});

const MODEL_ACCESS = Object.freeze({
    owner: "Owner",
    read: "Read",
    write: "Write",
});

const LOGIC_TYPE = Object.freeze({
    and: "and",
    or: "or",
});

const MODEL_TYPE = Object.freeze({
    eigenvector: "Eigenvector",
    standard: "Standard",
    calculatedFields: "CalculatedFields",
});

const MODEL_STATUS = Object.freeze({
    deployed: "Deployed",
    inProgress: "In Progress",
    completed: "Completed",
    notDeployed: "Not Deployed",
});

const ACCESS_OPTIONS = Object.freeze({
    read: { label: t("read"), value: "Read" },
    write: {
        label: t("readWrite"),
        value: "Read/Write",
    },
});

const NO_GROUP = Object.freeze({
    id: "bb0ec5b4-dafc-4c73-8a2a-08efd59feb7b",
});

const MEASUREMENT_METADATA = Object.freeze({
    sampleName: "sampleName",
    createdAt: "createdAt",
    scanner: "scanner",
    createdBy: "createdBy",
});

const SCANS_METADATA = Object.freeze({
    capturedAt: "capturedAt",
    scanner: "scanner",
    createdBy: "createdBy",
    onBehalfOf: "onBehalfOf",
    lot: "lot",
});

const DATE_FILTER_TYPE = Object.freeze({
    equal: "equal",
    inRange: "inRange",
    greaterThan: "greaterThan",
    lessThan: "lessThan",
});

const COMPARE_FILTER_TYPE = Object.freeze({
    equals: "equals",
    greaterThan: "greaterThan",
    lessThan: "lessThan",
    greaterThanOrEqual: "greaterThanOrEqual",
    lessThanOrEqual: "lessThanOrEqual",
});

const COMPARE_FILTER_TYPE_SIGN = Object.freeze({
    equals: "=",
    greaterThan: ">",
    lessThan: "<",
    greaterThanOrEqual: ">=",
    lessThanOrEqual: "<=",
});

const FILTER_TYPES = Object.freeze({
    text: "text",
    date: "date",
    compare: "compare",
    select: "select",
    number: "number",
    radio: "radio",
});

const TEXT_FILTER_TYPE = Object.freeze({
    equals: "equals",
    contains: "contains",
});

const METADATA_COLUMNS = Object.freeze({
    capturedAt: "Captured At",
    scanner: "Device Id",
    createdBy: "Created By",
    onBehalfOf: "On Behalf Of",
    lot: "Lot Name",
});

const COLLECT_METADATA_COLUMNS = Object.freeze({
    createdAt: "Created At",
    scanner: "Device Id",
    scannerId: "Device Id",
    createdBy: "Created By",
});

const DATES_CONSTANTS = Object.freeze({
    startDate: "startDate",
    endDate: "endDate",
});

const HTTP_METHODS = Object.freeze({
    get: "GET",
    post: "POST",
    put: "PUT",
    delete: "DELETE",
});

const ACTION_TYPE = Object.freeze({
    create: "Create",
    update: "Update",
    delete: "Delete",
    api: "API",
    error: "Error",
    mobile: "Mobile",
    other: "Other",
    integration: "Integration",
    get: "Get",
});

const ENTITY_TYPE = Object.freeze({
    model: "Model",
    user: "User",
    organization: "Organization",
    wavenumber: "Wavenumber",
    settings: "Settings",
    productSubscription: "Product Subscription",
    product: "Product",
    scanner: "Scanner",
    scan: "Scan",
    organizationIndustry: "Organization Industry",
    modelParameter: "Model Parameter",
    modelParameterVersion: "Model Parameter Version",
    modelUserAccess: "Model User Access",
    modelMeasurement: "Model Measurement",
    measurement: "Measurement",
    material: "Material",
    materialGroup: "Material Group",
    industry: "Industry",
    lot: "Lot",
    neoScan: "NeoScan",
    neoCollect: "NeoCollect",
    unknown: "Unknown",
    external: "External",
    support: "Support",
    dataRobot: "DataRobot",
    shopify: "Shopify",
});

const SUBSCRIPTION_TYPES = Object.freeze({
    monthly: "Monthly",
    annual: "Annual",
    trial: "Trial",
    custom: "Custom",
});

const CURRENCY_NAMES = Object.freeze({
    dollar: "Dollar",
    euro: "Euro",
});

const CURRENCY = Object.freeze({
    dollar: "USD",
    euro: "EUR",
});

const PRODUCT_TYPES = Object.freeze({
    model: "Model",
    bundle: "Bundle",
    single: "Single",
});

const MODEL_SCOPE = Object.freeze({
    global: "Global",
    local: "Local",
});

const SPECTRAL_TYPE = Object.freeze({
    readings: "readings",
    average: "average",
});

const COUPON_TYPES = Object.freeze({
    0: "Added Parameter",
    1: "Discount and Parameters",
    2: "Discount",
});

const ANSWERS = Object.freeze({
    yes: "Yes",
    no: "No",
});

const TRIAL_DAYS = Object.freeze({
    7: { label: "7", value: 7 },
    14: { label: "14", value: 14 },
    30: { label: "30", value: 30 },
});

const BOOLEAN_OPTIONS = Object.freeze({
    true: "true",
    false: "false",
});

const BOOLEAN_DROPDOWN = Object.freeze({
    true: { label: "True", value: true, booleanValue: true },
    false: { label: "False", value: false, booleanValue: false },
});

const SORTING_DIRECTION_OPTIONS = Object.freeze({
    ASC: { name: "ASC", label: "ASC", value: "ASC" },
    DESC: { name: "DESC", label: "DESC", value: "DESC" },
});

const STATUS_OPTIONS = Object.freeze({
    active: "Active",
    unpaid: "Unpaid",
    canceled: "Canceled",
    expired: "Expired",
});

const COLOR_PICKER_DEFAULTS = Object.freeze([
    "#3d4871",
    "#D9E3F0",
    "#F47373",
    "#697689",
    "#2CCCE4",
    "#555555",
    "#dce775",
    "#ff8a65",
]);

const GLOBAL_ID = 251;

const SPECTRUM_POINTS_BOUNDARY = 1500;

const SENTRY_URL = Object.freeze({
    dev: "https://7ff0b9fa2cd94de980efcfe2cf197f1a@o1011020.ingest.sentry.io/6153757",
    test: "https://7ff0b9fa2cd94de980efcfe2cf197f1a@o1011020.ingest.sentry.io/6153757",
    prod: "https://fb9b00bd69af4df4b26000f05385940d@o1011020.ingest.sentry.io/6158671",
});

const LABSTORE_ACCESS = Object.freeze({
    view: "Viewer",
    subscribe: "Subscriber",
    publish: "Publisher",
});

const PRODUCTS_STATUS = Object.freeze({
    accepted: "Accepted",
    rejected: "Rejected",
    deprecated: "Deprecated",
    underReview: "Under Review",
});

const TAGS_CLASSNAMES = Object.freeze({
    completed: "tag-completed",
    declined: "tag-declined",
    inProgress: "tag-in progress",
    undeployed: "tag-undeployed",
    deployed: "tag-deployed",
    bundle: "tag-bundle",
    unknown: "Unknown",
    trueCheck: "fas fa-check-circle",
    falseCheck: "fas fa-times-circle",
});

const API_VERSIONS = Object.freeze({
    v1: "v1",
});

const LANGUAGE = Object.freeze({
    English: "en",
    Portuguese: "pt",
    Spanish: "es",
    Chinese: "zh",
});

const UUIDS = Object.freeze({
    defaultScannerId: "8ae0f8e2-4779-429c-ac23-03b62c16bd02",
    aunirId: "ebb28465-59da-4d0c-9f51-aacb214802fb",
});

const UNITS = Object.freeze({
    percentage: { label: "%", value: 1 },
    empty_unit: { label: "None", value: 23 },
});

const MODEL_PROCESSING_TYPES = Object.freeze({
    Regression: {
        label: "Regression",
        value: "Regression",
    },
    Classification: {
        label: "Classification",
        value: "Classification",
    },
});

const LANGUAGE_FLAG_DATA = {
    en: { flag: "us", language: "English" },
    es: { flag: "es", language: "Spanish" },
    pt: { flag: "pt", language: "Portuguese" },
    zh: { flag: "cn", language: "Chinese" },
};

const DATA_TYPES = {
    number: "number",
    string: "string",
    object: "object",
    boolean: "boolean",
};

const FIELD_TYPES = Object.freeze({
    GridTextFields: "GridTextFields",
    TextArea: "TextArea",
    TextBox: "TextBox",
    Switch: "Switch",
    File: "File",
    Date: "Date",
    DateTime: "DateTime",
    Input: "Input",
    Dropdown: "Dropdown",
    RadioButton: "RadioButton",
    Compare: "Compare",
    EquationBuilder: "EquationBuilder",
    DoubleInput: "DoubleInput",
    Text: "text",
    CheckBoxes: "checkBoxes",
});

const OUTLIER_TYPES = Object.freeze({
    qresidual: { label: "Q-Residual", value: "QResidual" },
    reducedhotellingt2: {
        label: "Reduced Hotelling T2",
        value: "ReducedHotellingT2",
    },
});

const IMAGE_MAP = Object.freeze({
    users: "no-avatar.png",
    Suppliers: "org-avatar.png",
    lots: "location.png",
    affiliates: "org-avatar.png",
    subscriptions: "neural.png",
});

const SUBSCRIPTION_PRODUCT_TYPES = Object.freeze({
    license: "license",
    product: "product",
});

const AXIS_TYPES = Object.freeze({ X_AXIS: "xAxis", Y_AXIS: "yAxis" });

const X_AXIS_TYPES = Object.freeze({
    waveNumber: "wavenumber",
    waveLength: "wavelength",
});
const Y_AXIS_TYPES = Object.freeze({
    reflectance: "reflectance",
    absorbance: "absorbance",
});

const EXPORT_FORMATS = Object.freeze({
    csv: "csv",
    pdf: "pdf",
    xml: "xml",
});

const NOTIFICATION_TYPES = Object.freeze({
    email: "Email",
    system: "SystemNotification",
});

const ICON_TYPES = {
    warn: "exclamation-circle",
    warnTriangle: "exclamation-triangle",
    info: "info-circle",
    error: "exclamation-circle",
};

const PARAMETER_CONSTRAINTS_OPERATIONS = Object.freeze({
    min: "Min",
    max: "Max",
    eq: "Eq",
});

const NULL_OPTION = Object.freeze({ label: "--", value: null });

const MAX_UNITS_PER_PARAMETER = 10;
const MAX_PARAMETER = 30;

const MAX_LABELS_PER_CLASSIFIER = 10;
const MIN_LABELS_PER_CLASSIFIER = 2;

const EQUATION_CONSTANTS = Object.freeze({
    emptySpace: " ",
    openBracket: "(",
    closingBracket: ")",
    plus: "+",
    minus: "-",
    multiply: "*",
    divide: "/",
    comma: ",",
    left: "Left",
    right: "Right",
    function: "function",
    sign: "sign",
    binary: "binary",
});

const OPERATIONS_TYPE = Object.freeze({
    OPERATION: "arithmeticOperation",
    VARIABLE: "variable",
    CONSTANT: "constant",
    UNKNOWN: "unknown",
    OPEN_BRACKETS: "openBracket",
    CLOSE_BRACKETS: "closedBracket",
    INVALID: "invalid",
    FUNCTION: "function",
    GROUP: "group",
});

export {
    EQUATION_CONSTANTS,
    ORGANIZATION_PREFERENCES,
    ROLES,
    ROLES_VERSE,
    NO_GROUP,
    MODEL_ACCESS,
    MEASUREMENT_METADATA,
    DATE_FILTER_TYPE,
    COMPARE_FILTER_TYPE,
    FILTER_TYPES,
    TEXT_FILTER_TYPE,
    METADATA_COLUMNS,
    COLLECT_METADATA_COLUMNS,
    DATES_CONSTANTS,
    SCANS_METADATA,
    HTTP_METHODS,
    ACTION_TYPE,
    ENTITY_TYPE,
    SPECTRAL_TYPE,
    ACCESS_OPTIONS,
    MODEL_STATUS,
    SPECTRUM_POINTS_BOUNDARY,
    SENTRY_URL,
    SUBSCRIPTION_TYPES,
    COMPARE_FILTER_TYPE_SIGN,
    CURRENCY_NAMES,
    CURRENCY,
    PRODUCT_TYPES,
    MODEL_SCOPE,
    COUPON_TYPES,
    ANSWERS,
    TRIAL_DAYS,
    COLOR_PICKER_DEFAULTS,
    LABSTORE_ACCESS,
    GLOBAL_ID,
    PRODUCTS_STATUS,
    TAGS_CLASSNAMES,
    API_VERSIONS,
    BOOLEAN_OPTIONS,
    BOOLEAN_DROPDOWN,
    MODEL_TYPE,
    STATUS_OPTIONS,
    LANGUAGE,
    UUIDS,
    UNITS,
    MAX_UNITS_PER_PARAMETER,
    MAX_PARAMETER,
    MODEL_PROCESSING_TYPES,
    LANGUAGE_FLAG_DATA,
    MAX_LABELS_PER_CLASSIFIER,
    MIN_LABELS_PER_CLASSIFIER,
    FIELD_TYPES,
    DATA_TYPES,
    IMAGE_MAP,
    SUBSCRIPTION_PRODUCT_TYPES,
    OUTLIER_TYPES,
    LOGIC_TYPE,
    AXIS_TYPES,
    X_AXIS_TYPES,
    Y_AXIS_TYPES,
    NOTIFICATION_TYPES,
    NULL_OPTION,
    EXPORT_FORMATS,
    OPERATIONS_TYPE,
    ICON_TYPES,
    PREFERENCE_EMPTY_STATE,
    PARAMETER_CONSTRAINTS_OPERATIONS,
    SORTING_DIRECTION_OPTIONS,
};
